/**
 * Budget Settings — the single place budgets are configured and alerts are read.
 *
 * MODEL
 * Budgets are set **per account**. Provider totals and the tenant total are
 * DERIVED as sums of those, never set independently. That removes the question
 * "which budget wins when an account and its provider both have one" by making
 * it unanswerable — there is only ever one place a number is entered.
 *
 * WHY THIS MOVED HERE
 * Budget management used to live behind a "Manage Budget" button on the
 * Dashboard. Setting spending limits per account is an administrative act, not
 * something to do from an executive summary, and it needs the account list the
 * Dashboard has no reason to load.
 *
 * UNBUDGETED SPEND IS SHOWN, NOT HIDDEN
 * If some accounts have no budget, the derived provider total understates
 * reality — those accounts still spend. Silently summing would make a provider
 * look healthier than it is, so the coverage ("4 of 6 accounts budgeted") is
 * stated plainly. Same principle as unallocated spend in chargeback.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, Check, AlertTriangle, BellRing, Loader2 } from 'lucide-react';
import { authHeaders, withFreshData } from '../../services/apiHeaders';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';

const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

type Provider = 'aws' | 'azure' | 'gcp';
const PROVIDERS: Provider[] = ['aws', 'azure', 'gcp'];
const PROVIDER_LABEL: Record<Provider, string> = { aws: 'AWS', azure: 'Azure', gcp: 'GCP' };

interface ScopeAccount {
  accountId: string;
  accountName: string;
  provider: Provider;
  accountType: string;
}

interface BudgetRow {
  id: string;
  amount: number;
  scope: { type: string; value?: string; provider?: Provider };
}

interface BudgetAlert {
  id: string;
  budgetId: string;
  threshold: number;
  triggeredAt: string;
  actualAmount: number;
  projectedAmount?: number;
  acknowledgedAt?: string | null;
  budgetName?: string;
}

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const money = (v: number) =>
  `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const providerVariant = (p: Provider) =>
  p === 'aws' ? 'warning' : p === 'azure' ? 'info' : 'success';

export function BudgetSettingsTab() {
  const [accounts, setAccounts] = useState<ScopeAccount[]>([]);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [existing, setExisting] = useState<Record<string, BudgetRow>>({});
  const [alerts, setAlerts] = useState<BudgetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [acking, setAcking] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, AccountHistory>>({});
  const [historyLoading, setHistoryLoading] = useState(true);

  /** Budgets are keyed by provider + account, matching the backend scope. */
  const keyOf = (p: Provider, accountId: string) => `${p}:${accountId}`;

  /**
   * Recent spend per account, so a budget is set against what the account
   * actually costs rather than from memory.
   *
   * DEFINITIONS, and why each is what it is:
   *
   * "Last month" is the last COMPLETE calendar month, never the current one.
   * On the 29th, month-to-date is roughly the full month and looks fine; on
   * the 2nd it is near zero. A number whose meaning depends on today's date
   * is worse than no number when someone is about to commit to a budget from
   * it.
   *
   * "6-month average" is the trailing six COMPLETE months, ending with that
   * same last month. It deliberately overlaps the previous column: shown
   * together, the pair answers the only question that matters here - was last
   * month typical, or an outlier?
   *
   * The divisor is the number of months that actually HAVE data, not a flat
   * six. An account onboarded three months ago would otherwise have its
   * average halved, and a budget set from that would be far too low. Where
   * fewer than six months exist the count is shown, so the figure is never
   * mistaken for a full six-month average.
   *
   * A month present in the data with zero cost counts as a real zero - an
   * idle account genuinely averaged nothing. A month with no rows at all is
   * excluded from the divisor, because that is missing history, not $0 spent.
   */
  interface AccountHistory {
    lastMonth: number | null;
    average: number | null;
    monthsCounted: number;
  }

  /** First day of the month `n` months before the current one, as YYYY-MM-DD. */
  const monthStart = (monthsBack: number): string => {
    const d = new Date();
    const dt = new Date(d.getFullYear(), d.getMonth() - monthsBack, 1);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
  };

  const loadHistory = async (accs: ScopeAccount[]) => {
    if (accs.length === 0) {
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    try {
      // ONE request for every account and every month, rather than six per
      // account. With 41 accounts the per-month-per-account shape would be
      // ~250 Athena round trips for a table that has to render promptly.
      //
      // Window: the start of the month six months back, up to the start of the
      // CURRENT month - which excludes the partial current month entirely,
      // because `end` is exclusive.
      const start = monthStart(6);
      const end = monthStart(0);
      const res = await fetch(
        `${COST_API_URL}/cost?start=${start}T00:00:00Z&end=${end}T00:00:00Z` +
          // Same cost basis as the Invoice page. Budgets are compared against
          // what is actually billed, so anything else would have the user
          // budgeting against a number they never receive an invoice for.
          // Monthly buckets x accounts is at most a few hundred rows, but the
          // ceiling is raised anyway: pagination TRUNCATES rather than erroring,
          // and it keeps the OLDEST rows, so an under-sized page silently
          // returns the wrong months instead of failing visibly. That is
          // precisely how this first shipped showing "(1 mo)" everywhere.
          `&granularity=monthly&group_by=account&cost_view=net_unblended&currency=USD&page_size=5000`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`cost history ${res.status}`);
      const body = await res.json();
      const rows: any[] = body.data || [];

      // account id (lower-cased) -> month key -> cost.
      // Lower-cased because Azure subscription GUIDs do not arrive in a
      // consistent case between the cost export and the accounts table.
      const byAccount: Record<string, Record<string, number>> = {};
      for (const row of rows) {
        const acct = String(row.dimensions?.account || '').toLowerCase();
        if (!acct) continue;
        const month = String(row.timestamp || '').slice(0, 7);
        if (!month) continue;
        byAccount[acct] = byAccount[acct] || {};
        byAccount[acct][month] = (byAccount[acct][month] || 0) + (Number(row.cost) || 0);
      }

      const lastMonthKey = monthStart(1).slice(0, 7);
      const next: Record<string, AccountHistory> = {};
      for (const acc of accs) {
        const months = byAccount[acc.accountId.toLowerCase()];
        if (!months) {
          next[keyOf(acc.provider, acc.accountId)] = {
            lastMonth: null,
            average: null,
            monthsCounted: 0,
          };
          continue;
        }
        const values = Object.values(months);
        next[keyOf(acc.provider, acc.accountId)] = {
          lastMonth: months[lastMonthKey] ?? null,
          average: values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null,
          monthsCounted: values.length,
        };
      }
      setHistory(next);
    } catch {
      // Leave the columns empty rather than failing the tab. Budgets must stay
      // editable even when the cost warehouse is slow or unreachable.
      setHistory({});
    } finally {
      setHistoryLoading(false);
    }
  };


  const load = async () => {
    setLoading(true);
    try {
      const h = authHeaders();
      const [accRes, budRes, alertRes] = await Promise.all([
        fetch(`${COST_API_URL}/config/budget-scopes`, { headers: h }),
        fetch(`${COST_API_URL}/config/budgets?period=${currentPeriod()}&scope_type=account`, { headers: h }),
        fetch(`${COST_API_URL}/config/budget-alerts`, { headers: h }).catch(() => null as any),
      ]);

      const accs: ScopeAccount[] = accRes.ok ? (await accRes.json()).data || [] : [];
      setAccounts(accs);

      // Deliberately NOT awaited. The spend history is an Athena query over six
      // months and can take seconds; the budget inputs must be usable straight
      // away rather than held behind it. The two columns fill in when it lands.
      void loadHistory(accs);

      const budBody = budRes.ok ? await budRes.json() : { data: {} };
      const rows: BudgetRow[] = budBody.data?.budgets || budBody.data || [];
      const byKey: Record<string, BudgetRow> = {};
      const amt: Record<string, string> = {};
      for (const b of Array.isArray(rows) ? rows : []) {
        const p = b.scope?.provider;
        const v = b.scope?.value;
        if (!p || !v) continue;
        byKey[keyOf(p, v)] = b;
        amt[keyOf(p, v)] = String(b.amount);
      }
      setExisting(byKey);
      setAmounts(amt);

      if (alertRes && alertRes.ok) {
        const ab = await alertRes.json();
        setAlerts(ab.data || []);
      }
    } catch (e: any) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    let written = 0;
    try {
      for (const acc of accounts) {
        const k = keyOf(acc.provider, acc.accountId);
        const raw = (amounts[k] ?? '').trim();
        const value = raw === '' ? 0 : Number(raw);
        const prior = existing[k];

        if (!raw || value <= 0) {
          // Clearing the field removes the budget rather than storing zero —
          // "no budget" and "a budget of nothing" are different statements and
          // a £0 budget would read as permanently 100% breached.
          if (prior) {
            await fetch(`${COST_API_URL}/config/budgets/${prior.id}`, {
              method: 'DELETE',
              headers: authHeaders(),
            });
          }
          continue;
        }

        await fetch(`${COST_API_URL}/config/budgets`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            period: currentPeriod(),
            amount: value,
            currency: 'USD',
            scope: { type: 'account', value: acc.accountId, provider: acc.provider },
          }),
        });
        written++;
      }
      setMsg({ kind: 'ok', text: `Saved ${written} budget${written === 1 ? '' : 's'}.` });
      await withFreshData(() => load());
    } catch (e: any) {
      setMsg({ kind: 'err', text: `Could not save: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  const acknowledge = async (id: string) => {
    setAcking(id);
    try {
      await fetch(`${COST_API_URL}/config/budget-alerts/${id}/acknowledge`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      setAlerts((prev) =>
        prev.map((a) => (a.id === id ? { ...a, acknowledgedAt: new Date().toISOString() } : a))
      );
    } finally {
      setAcking(null);
    }
  };

  // Derived totals — never entered by hand.
  const providerTotal = (p: Provider) =>
    accounts
      .filter((a) => a.provider === p)
      .reduce((s, a) => s + (Number(amounts[keyOf(p, a.accountId)]) || 0), 0);

  const coverage = (p: Provider) => {
    const list = accounts.filter((a) => a.provider === p);
    const withBudget = list.filter((a) => Number(amounts[keyOf(p, a.accountId)]) > 0).length;
    return { withBudget, total: list.length };
  };

  const tenantTotal = PROVIDERS.reduce((s, p) => s + providerTotal(p), 0);
  const openAlerts = alerts.filter((a) => !a.acknowledgedAt).length;

  if (loading) {
    return <Card className="p-6 text-sm text-muted-foreground">Loading budgets…</Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Budget Settings</h2>
          <p className="text-sm text-muted-foreground">
            Set a monthly budget per account. Provider and overall totals are calculated from
            these — they are not set separately.
          </p>
          {/* Say WHY the spend columns are empty when they are.
              Two blank columns read as a broken feature; the real reason is
              that the cost export simply has no completed month yet, and it
              will fill itself in without anyone doing anything. Only shown
              when every account is empty - a single account with no history
              among many is a different situation and needs no banner. */}
          {!historyLoading &&
            accounts.length > 0 &&
            Object.values(history).every((h) => h.monthsCounted === 0) && (
              <p className="mt-2 text-xs text-muted-foreground">
                Last month and 6-month average are blank because the cost export has no
                completed calendar month of data yet. They populate automatically once the
                first full month is available.
              </p>
            )}
        </div>
        <Button size="sm" variant="outline" onClick={() => withFreshData(() => load())}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {msg && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            msg.kind === 'ok'
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
              : 'bg-rose-500/10 text-rose-700 dark:text-rose-400'
          }`}
        >
          {msg.text}
        </div>
      )}

      {accounts.length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">
          No cloud accounts are onboarded yet. Add one under <strong>Cloud Onboarding</strong>,
          then set its budget here.
        </Card>
      )}

      {/* Overall total, derived */}
      {accounts.length > 0 && (
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Total monthly budget ({currentPeriod()})
          </div>
          <div className="mt-0.5 text-3xl font-semibold">{money(tenantTotal)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Sum of every account budget below
          </div>
        </Card>
      )}

      {/* Per provider, per account */}
      {PROVIDERS.filter((p) => accounts.some((a) => a.provider === p)).map((p) => {
        const cov = coverage(p);
        const gap = cov.total - cov.withBudget;
        return (
          <Card key={p} className="p-0 overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <div className="flex items-center gap-2">
                <Badge variant={providerVariant(p) as any}>{PROVIDER_LABEL[p]}</Badge>
                <span className="text-sm text-muted-foreground">
                  {cov.withBudget} of {cov.total} accounts budgeted
                </span>
                {gap > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    {gap} without a budget — their spend is not covered by this total
                  </span>
                )}
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Provider total </span>
                <span className="font-semibold tabular-nums">{money(providerTotal(p))}</span>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Account</th>
                  <th className="px-4 py-2 text-left font-medium">Identifier</th>
                  {/* Actuals sit immediately left of the input on purpose - the
                      budget is decided by reading across from them. */}
                  <th
                    className="px-4 py-2 text-right font-medium"
                    title="Net spend for the last complete calendar month. Excludes the current, partial month."
                  >
                    Last month
                  </th>
                  <th
                    className="px-4 py-2 text-right font-medium"
                    title="Average net spend across the last six complete months. Where fewer months of data exist, the average is over those months and the count is shown."
                  >
                    6-mo average
                  </th>
                  <th className="px-4 py-2 text-right font-medium">Monthly budget (USD)</th>
                </tr>
              </thead>
              <tbody>
                {accounts
                  .filter((a) => a.provider === p)
                  .map((a) => {
                    const k = keyOf(p, a.accountId);
                    const h = history[k];
                    const entered = parseFloat(amounts[k] ?? '');
                    // The mistake this feature exists to catch: budgeting less
                    // than the account already spends every month. Flagged only
                    // when both numbers are real, so it cannot fire on missing
                    // history or an empty input.
                    const underLastMonth =
                      Number.isFinite(entered) &&
                      entered > 0 &&
                      h?.lastMonth != null &&
                      entered < h.lastMonth;
                    return (
                      <tr key={k} className="border-t border-border">
                        <td className="px-4 py-2">
                          {a.accountName || a.accountId}
                          {a.accountType === 'master' && (
                            <span className="ml-2 text-xs text-muted-foreground">(master)</span>
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                          {a.accountId}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {historyLoading ? (
                            <span className="text-muted-foreground">…</span>
                          ) : h?.lastMonth != null ? (
                            money(h.lastMonth)
                          ) : (
                            // Not "$0" - no data and no spend are different
                            // things, and only one of them justifies a $0 budget.
                            <span className="text-muted-foreground" title="No cost data for this account in that month">
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {historyLoading ? (
                            <span className="text-muted-foreground">…</span>
                          ) : h?.average != null ? (
                            <>
                              {money(h.average)}
                              {h.monthsCounted < 6 && (
                                <span
                                  className="ml-1 text-xs text-muted-foreground"
                                  title={`Only ${h.monthsCounted} month(s) of data available, so this is an average over ${h.monthsCounted}, not 6`}
                                >
                                  ({h.monthsCounted} mo)
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            step={100}
                            inputMode="decimal"
                            placeholder="none"
                            value={amounts[k] ?? ''}
                            onChange={(e) =>
                              setAmounts((prev) => ({ ...prev, [k]: e.target.value }))
                            }
                            className={`w-40 rounded-md border bg-card px-2 py-1 text-right tabular-nums focus:outline-none focus:ring-1 ${
                              underLastMonth
                                ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-400'
                                : 'border-border focus:border-ring focus:ring-ring'
                            }`}
                          />
                          {underLastMonth && (
                            // A warning, not a block. Budgeting below recent
                            // spend is sometimes exactly the intent - a
                            // deliberate target to drive the account down - so
                            // this says what will happen rather than refusing.
                            <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                              Below last month&apos;s spend — expect alerts
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </Card>
        );
      })}

      {accounts.length > 0 && (
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={saving} size="sm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save budgets'}
          </Button>
          <span className="text-xs text-muted-foreground">
            Clearing a field removes that account's budget.
          </span>
        </div>
      )}

      {/* Alerts — previously a separate top-level page, and before that a tab
          inside the Dashboard's budget dialog. They belong beside the budgets
          that generate them. */}
      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Budget alerts</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {alerts.length} total · {openAlerts} unacknowledged
          </span>
        </div>

        {alerts.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            No alerts yet. They appear here when an account's spend crosses a threshold of its
            budget.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Budget</th>
                <th className="px-4 py-2 text-left font-medium">Threshold</th>
                <th className="px-4 py-2 text-right font-medium">Actual</th>
                <th className="px-4 py-2 text-left font-medium">Triggered</th>
                <th className="px-4 py-2 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {[...alerts]
                .sort((a, b) => {
                  // Unacknowledged first regardless of age: an old unhandled
                  // breach matters more than a recent one already seen.
                  const aa = a.acknowledgedAt ? 1 : 0;
                  const bb = b.acknowledgedAt ? 1 : 0;
                  if (aa !== bb) return aa - bb;
                  return new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime();
                })
                .map((a) => (
                  <tr
                    key={a.id}
                    className={`border-t border-border ${a.acknowledgedAt ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-2">{a.budgetName || 'Budget'}</td>
                    <td className="px-4 py-2">
                      <Badge variant={a.threshold >= 100 ? 'danger' : 'warning'}>
                        {a.threshold}%
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(a.actualAmount)}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(a.triggeredAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {a.acknowledgedAt ? (
                        <span className="text-xs text-muted-foreground">Acknowledged</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={acking === a.id}
                          onClick={() => acknowledge(a.id)}
                        >
                          {acking === a.id ? 'Saving…' : 'Acknowledge'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
